import { World } from '@ecs/World';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Collider } from '@components/Collider';
import { Tag } from '@components/Tag';
import { Selectable } from '@components/Selectable';
import { ShipClass } from '@components/ShipClass';
import { PlayerInput } from '@components/PlayerInput';
import { SelectionBox } from '@components/SelectionBox';
import { getFormationOffsets } from '@utils/Formations';
import { Effects } from '@/effects/Effects';

export interface PointerInfo {
  x: number;
  y: number;
  button: 'primary' | 'secondary';
  shift?: boolean;
  touch?: boolean;
}

export interface KeyModifiers {
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

const DRAG_THRESHOLD = 6;
const DOUBLE_CLICK_MS = 350;

/**
 * RTS-style fleet control.
 *
 * Mouse: left-click / drag selects (shift adds), right-click moves or attacks,
 * left-clicking an enemy also attacks, double-click selects a whole ship class.
 * Touch: tap a ship to select it, drag to box-select, tap space to move and
 * tap an enemy to attack.
 */
export class CommandController {
  private dragStart: PointerInfo | null = null;
  private dragging = false;
  private lastClick: { id: number; time: number } | null = null;
  private groups: Record<number, number[]> = {};

  constructor(private readonly world: World) {}

  // ---------------------------------------------------------------------------
  // Pointer input

  pointerDown(pointer: PointerInfo): void {
    if (pointer.button !== 'primary') return;
    this.dragStart = pointer;
    this.dragging = false;
  }

  pointerMove(x: number, y: number): void {
    if (!this.dragStart) return;

    if (!this.dragging && Math.hypot(x - this.dragStart.x, y - this.dragStart.y) > DRAG_THRESHOLD) {
      this.dragging = true;
    }

    if (this.dragging) {
      const box = this.getSelectionBox();
      box.active = true;
      box.x1 = this.dragStart.x;
      box.y1 = this.dragStart.y;
      box.x2 = x;
      box.y2 = y;
    }
  }

  pointerUp(pointer: PointerInfo): void {
    if (pointer.button === 'secondary') {
      this.issueCommand(pointer.x, pointer.y);
      return;
    }

    const start = this.dragStart;
    this.dragStart = null;

    if (this.dragging && start) {
      this.dragging = false;
      this.getSelectionBox().active = false;
      this.boxSelect(start.x, start.y, pointer.x, pointer.y, pointer.shift ?? false);
      return;
    }

    this.primaryClick(pointer);
  }

  isDragging(): boolean {
    return this.dragging;
  }

  // ---------------------------------------------------------------------------
  // Selection

  getSelected(): Entity[] {
    return this.playerShips().filter(ship => ship.getComponent(Selectable)!.selected);
  }

  selectAll(): void {
    this.playerShips().forEach(ship => { ship.getComponent(Selectable)!.selected = true; });
  }

  clearSelection(): void {
    this.playerShips().forEach(ship => { ship.getComponent(Selectable)!.selected = false; });
  }

  /** Selects exactly the given ships, or adds them to the selection. */
  select(ships: Entity[], additive: boolean = false): void {
    if (!additive) this.clearSelection();
    ships.forEach(ship => {
      const selectable = ship.getComponent(Selectable);
      if (selectable) selectable.selected = true;
    });
  }

  toggle(ship: Entity): void {
    const selectable = ship.getComponent(Selectable);
    if (selectable) selectable.selected = !selectable.selected;
  }

  // ---------------------------------------------------------------------------
  // Orders

  orderMove(x: number, y: number): void {
    const ships = this.getSelected();
    if (ships.length === 0) return;

    const offsets = getFormationOffsets(ships.length);
    ships.forEach((ship, index) => {
      const input = ship.getComponent(PlayerInput)!;
      input.targetPosition = { x: x + offsets[index].x, y: y + offsets[index].y };
      input.targetEntity = null;
      input.pursueTarget = false;
    });

    Effects.orderMarker(this.world, x, y, '#00ff88');
  }

  orderAttack(enemy: Entity): void {
    const ships = this.getSelected();
    if (ships.length === 0) return;

    ships.forEach(ship => {
      const input = ship.getComponent(PlayerInput)!;
      input.targetEntity = enemy.id;
      input.pursueTarget = true;
      input.targetPosition = null;
    });

    const pos = enemy.getComponent(Position)!;
    Effects.orderMarker(this.world, pos.x, pos.y, '#ff5555');
  }

  // ---------------------------------------------------------------------------
  // Control groups

  assignGroup(group: number): void {
    const ids = this.getSelected().map(ship => ship.id);
    if (ids.length === 0) {
      delete this.groups[group];
    } else {
      this.groups[group] = ids;
    }
  }

  recallGroup(group: number): void {
    const alive = (this.groups[group] ?? [])
      .map(id => this.world.getEntity(id))
      .filter((ship): ship is Entity => ship !== undefined && !this.world.isPendingRemoval(ship));

    if (alive.length === 0) {
      delete this.groups[group];
      return;
    }

    this.groups[group] = alive.map(ship => ship.id);
    this.select(alive);
  }

  getGroups(): Record<number, number[]> {
    // Prune destroyed ships so the HUD stays accurate
    for (const key of Object.keys(this.groups)) {
      const group = Number(key);
      this.groups[group] = this.groups[group].filter(id => this.world.getEntity(id) !== undefined);
      if (this.groups[group].length === 0) delete this.groups[group];
    }
    return { ...this.groups };
  }

  reset(): void {
    this.groups = {};
    this.lastClick = null;
    this.dragStart = null;
    this.dragging = false;
  }

  /** Returns true when the key was a fleet command. */
  handleKey(key: string, modifiers: KeyModifiers): boolean {
    const command = modifiers.ctrl || modifiers.meta;

    if (command && key.toLowerCase() === 'a') {
      this.selectAll();
      return true;
    }
    if (key === 'Escape') {
      this.clearSelection();
      return true;
    }
    if (/^[1-9]$/.test(key)) {
      const group = Number(key);
      if (command) this.assignGroup(group);
      else this.recallGroup(group);
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Internals

  private primaryClick(pointer: PointerInfo): void {
    const ship = this.pickShip(pointer.x, pointer.y, pointer.touch ?? false);
    if (ship) {
      const now = Date.now();
      const isDoubleClick = this.lastClick?.id === ship.id && now - this.lastClick.time < DOUBLE_CLICK_MS;
      this.lastClick = { id: ship.id, time: now };

      if (isDoubleClick) {
        this.selectClassOf(ship);
      } else if (pointer.shift) {
        this.toggle(ship);
      } else {
        this.select([ship]);
      }
      return;
    }

    this.lastClick = null;

    const enemy = this.pickEnemy(pointer.x, pointer.y, pointer.touch ?? false);
    if (enemy) {
      this.orderAttack(enemy);
      return;
    }

    if (pointer.touch) {
      this.orderMove(pointer.x, pointer.y);
    } else if (!pointer.shift) {
      this.clearSelection();
    }
  }

  private issueCommand(x: number, y: number): void {
    const enemy = this.pickEnemy(x, y, false);
    if (enemy) this.orderAttack(enemy);
    else this.orderMove(x, y);
  }

  selectClassOf(ship: Entity): void {
    const shipClass = ship.getComponent(ShipClass)?.value;
    this.select(this.playerShips().filter(s => s.getComponent(ShipClass)?.value === shipClass));
  }

  private boxSelect(x1: number, y1: number, x2: number, y2: number, additive: boolean): void {
    const [left, right] = [Math.min(x1, x2), Math.max(x1, x2)];
    const [top, bottom] = [Math.min(y1, y2), Math.max(y1, y2)];
    const inside = this.playerShips().filter(ship => {
      const pos = ship.getComponent(Position)!;
      return pos.x >= left && pos.x <= right && pos.y >= top && pos.y <= bottom;
    });
    this.select(inside, additive);
  }

  private playerShips(): Entity[] {
    return this.world.getEntitiesWithComponents(Selectable, Position, Tag)
      .filter(ship => ship.getComponent(Tag)!.value === 'player' && !this.world.isPendingRemoval(ship));
  }

  private pickShip(x: number, y: number, touch: boolean): Entity | null {
    return this.pickNearest(this.playerShips(), x, y, touch ? 14 : 6);
  }

  private pickEnemy(x: number, y: number, touch: boolean): Entity | null {
    const enemies = this.world.getEntitiesWithComponents(Position, Tag)
      .filter(e => e.getComponent(Tag)!.value === 'enemy' && !this.world.isPendingRemoval(e));
    return this.pickNearest(enemies, x, y, touch ? 20 : 12);
  }

  private pickNearest(candidates: Entity[], x: number, y: number, slack: number): Entity | null {
    let best: Entity | null = null;
    let bestDistance = Infinity;
    for (const entity of candidates) {
      const pos = entity.getComponent(Position)!;
      const radius = entity.getComponent(Collider)?.radius ?? 10;
      const distance = Math.hypot(pos.x - x, pos.y - y);
      if (distance <= radius + slack && distance < bestDistance) {
        best = entity;
        bestDistance = distance;
      }
    }
    return best;
  }

  private getSelectionBox(): SelectionBox {
    const existing = this.world.getEntitiesWithComponents(SelectionBox)[0];
    if (existing) return existing.getComponent(SelectionBox)!;
    const box = new SelectionBox();
    this.world.addEntity(new Entity().addComponent(box));
    return box;
  }
}
